// PlayNowOverlay — a prominent one-shot landing CTA that invites first-time
// visitors to jump straight into the Sports Arena instead of scrolling the
// campus looking for it. Renders as a large centered card on load; once the
// visitor either taps PLAY NOW or dismisses it, it's remembered in
// localStorage so we don't shove it in front of returning members.
//
// The button calls arenaApi.teleportRef.current.teleport() — the same path
// as the HUD's "Go to Arena" pill, so a single code path owns the entry
// pose (camera at [tx+0.2, 12.5, tz+22] + walker at plaza).
//
// On coarse-pointer devices we additionally publish a global
// `window.__zone_enter_walk()` from ZoneScene; when Play Now fires we call
// it so the touch joystick appears immediately — the visitor lands on the
// court already able to move.

import React, { useEffect, useState } from "react";
import { MapPin, X, Volleyball } from "lucide-react";

import { COLORS } from "../constants";

const DISMISS_KEY = "tab-playnow-dismissed-v1";

export default function PlayNowOverlay({ arenaApi, isTouch }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISS_KEY) === "1";
    } catch (e) {
      /* private-mode Safari — treat as not-dismissed */
    }
    if (!dismissed) {
      // Small delay so it fades in after the campus paints, not on top of
      // the initial Loading… splash.
      const id = window.setTimeout(() => setVisible(true), 900);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  const handlePlay = (sport) => {
    // Sport-aware entry: pick the sport in the arena state (so the ball,
    // click-plane, crowd re-anchor before the camera settles) and jump the
    // camera *onto* that field. If no sport is passed we default to the
    // entrance plaza teleport for backward compatibility.
    if (sport && typeof arenaApi.setSport === "function") {
      arenaApi.setSport(sport);
    }
    const t = arenaApi?.teleportRef?.current?.teleport;
    if (typeof t === "function") {
      try {
        if (sport) {
          // Coordinates mirror SPORT_CENTERS in arenaState.js
          const target =
            sport === "basketball" ? [45, 0, 20]
            : sport === "soccer"    ? [70, 0, 40]
            : sport === "football"  ? [40, 0, 70]
            : sport === "baseball"  ? [-45, 0, 55]
            : null;
          t(target);
        } else {
          t();
        }
      } catch (e) {
        /* ignore */
      }
    }
    if (isTouch) {
      try {
        if (typeof window.__zone_enter_walk === "function") {
          window.__zone_enter_walk();
        }
      } catch (e) {
        /* ignore */
      }
    }
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="playnow-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 90,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(11,21,48,0.55)",
        backdropFilter: "blur(4px)",
        animation: "fadeIn 340ms ease-out",
      }}
    >
      <div
        style={{
          position: "relative",
          maxWidth: 420,
          width: "88%",
          padding: "28px 26px 24px",
          borderRadius: 18,
          background:
            "linear-gradient(180deg, rgba(11,21,48,0.94) 0%, rgba(6,14,32,0.94) 100%)",
          border: `1px solid ${COLORS.gold}`,
          boxShadow: "0 24px 56px rgba(0,0,0,0.5), 0 0 0 6px rgba(230,180,80,0.08)",
          textAlign: "center",
          color: "#f3ead4",
        }}
      >
        <button
          data-testid="playnow-close"
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            padding: 6,
            background: "transparent",
            border: "none",
            color: "#c9b382",
            cursor: "pointer",
            opacity: 0.75,
          }}
        >
          <X size={18} />
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: 11,
            letterSpacing: 2,
            color: "#c9b382",
            fontWeight: 700,
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          <Volleyball size={14} />
          GAMEDAY
        </div>

        <h2
          style={{
            margin: 0,
            fontFamily: "'Cormorant Garamond','Playfair Display',serif",
            fontSize: 34,
            fontWeight: 600,
            lineHeight: 1.05,
            color: "#f7dc95",
          }}
        >
          Ready to play?
        </h2>
        <p
          style={{
            margin: "10px 6px 20px",
            fontSize: 14,
            lineHeight: 1.55,
            color: "#dcd3b8",
            opacity: 0.9,
          }}
        >
          Jump straight to your sport — pick one to enter the field with the
          right ball, crowd, and CPU opponent already waiting.
        </p>

        {/* Four sport chips — clicking one sets the active sport in the HUD
            AND teleports the camera directly onto that field. Icons are
            emoji so we don't hit the drei-font warmup cost from lucide. */}
        <div
          data-testid="playnow-sport-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginBottom: 12,
          }}
        >
          {[
            { id: "basketball", label: "Basketball", icon: "🏀", color: "#f26a34" },
            { id: "soccer",     label: "Soccer",     icon: "⚽", color: "#4bb352" },
            { id: "football",   label: "Football",   icon: "🏈", color: "#7d4dd6" },
            { id: "baseball",   label: "Baseball",   icon: "⚾", color: "#c8353a" },
          ].map((s) => (
            <button
              key={s.id}
              data-testid={`playnow-sport-${s.id}`}
              onClick={() => handlePlay(s.id)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                padding: "14px 10px",
                borderRadius: 12,
                border: `1px solid ${s.color}`,
                background: "linear-gradient(180deg, rgba(11,21,48,0.7) 0%, rgba(6,14,32,0.85) 100%)",
                color: "#f3ead4",
                fontFamily: "'Cormorant Garamond','Playfair Display',serif",
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                cursor: "pointer",
                boxShadow: `0 6px 14px ${s.color}22`,
                transition: "transform 120ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <span style={{ fontSize: 32, lineHeight: 1 }}>{s.icon}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        <button
          data-testid="playnow-btn"
          onClick={() => handlePlay()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            width: "100%",
            padding: "11px 16px",
            borderRadius: 12,
            border: `1px solid ${COLORS.gold}`,
            background: "linear-gradient(180deg,#f7dc95 0%,#e6b450 50%,#c8933a 100%)",
            color: "#111827",
            fontFamily: "'Cormorant Garamond','Playfair Display',serif",
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 2,
            textTransform: "uppercase",
            cursor: "pointer",
            boxShadow: "0 8px 20px rgba(230,180,80,0.35)",
          }}
        >
          <MapPin size={14} />
          Or drop me at the plaza
        </button>

        <button
          data-testid="playnow-explore"
          onClick={dismiss}
          style={{
            marginTop: 10,
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            background: "transparent",
            border: "1px solid rgba(230,180,80,0.28)",
            color: "#c9b382",
            fontSize: 12,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Explore the quad instead
        </button>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}
